import { useEffect, useRef, useState } from "react";
import {
  getDatabase,
  ref,
  query,
  onChildAdded,
  limitToLast,
  orderByChild,
  startAt,
} from "firebase/database";
import { database } from "src/authentication/firebase";
import { useDispatch, useSelector } from "react-redux";
import { clearChatList, addChatMessage, setChatUsername } from "src/store/dashboardSlice";
import apiServices from "src/api/apiService";
import { getPublicMessages } from "src/thunks/dashboardThunk";

const PublicChat = () => {
  //A public chat widget to allow any visitors to post a message to the public chat.
  // A placeholder of predefined chats are displayed like "make a anonymous public message!"
  //initially the whole textbox and send button are covered by a button saying "Send a public chat!"
  //When selected, the widget changes to display a random list of selectable tagged words (select 2-4 as validation) as a 'username' of the message and done button.
  //while user is doing that, public chat attempts connection to the socket.
  //on success show the loaded messages(maybe temporary memory of backend) and textbox and sendbutton. on error display an error message.
  //use local storage to prevent multiple windows chat exploit.
  //backend will do slight content moderation/spam detection and rate limiting.
  const [chatStatus, setChatStatus] = useState("init");
  const [listening, setListening] = useState(false);
  const [unsubscribe, setUnsubscribe] = useState(null);

  const chatDetails = useSelector((state) => state.dashboard.chat);
  const loggedInUser = useSelector((state) => state.auth.userData);
  const anonUserNumber = useSelector((state) => state.dashboard?.naruhone?.naruhone?.anonUserNumber);

  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);

  //Chat behaviour to autoscroll the window to show new chats.
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const usernameRef = useRef(chatDetails.username); // Store latest username

  const [hasJustLoadedChat, setHasJustLoadedChat] = useState(false);
  const [hasJustSentMessage, setHasJustSentMessage] = useState(false);
  const [isNewChat, setNewChat] = useState(false);
  // Function to check if the user is at the bottom
  const checkIfAtBottom = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        chatContainerRef.current;
        if(scrollTop + clientHeight >= scrollHeight - 10){
          isAtBottomRef.current = true;
          if(isNewChat){
            setNewChat(false);
          }
        }else{
          isAtBottomRef.current = false;
        }
       // Small offset for precision
    }
  };
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      isAtBottomRef.current = true;
    }
  };

  //Format the timestamp for the chat bubbles
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const dispatch = useDispatch();


  useEffect(() => {
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [unsubscribe]);
   useEffect(() => {
    if (hasJustSentMessage === true || hasJustLoadedChat === true || isAtBottomRef.current === true) {
      setHasJustSentMessage(false);
      setHasJustLoadedChat(false);
      setTimeout(() => {
        scrollToBottom();
      }, 400);
    
    }
  }, [chatDetails.messageList]);

  useEffect(() => {
    usernameRef.current = chatDetails.username; // Update ref whenever username changes
}, [chatDetails.username]);

  const getPublicChat = () => {
    dispatch(clearChatList());
    dispatch(getPublicMessages());
    // dispatch(setChatUsername("testuser"));
    if (!listening) {
      // Start listening
      const now = Date.now();

const liveQuery = query(
  ref(database, "messages/public"),
  orderByChild('timestamp'),
  startAt(now + 1) // strictly after now
);

      const listener = onChildAdded(liveQuery, (snapshot) => {
        const newMessage = snapshot.val();
        dispatch(addChatMessage(newMessage));
        

        if(!isAtBottomRef.current && newMessage.username !== usernameRef.current){
          setNewChat(true);
        }
      });

      // Save the unsubscribe function
      setUnsubscribe(() => listener);
      setListening(true);
    }
  };
  
  const handleKeyDown = async (e) => {
    
    if (e.key === "Enter" && message.trim()) {
      setHasJustSentMessage(true);
      let response = await apiServices.dashboard.sendPublicMessage(chatDetails.username, message);
      if(!response.ok){
        let responseJson = await response.json();
        if (responseJson.error != null) {
          setErrorMessage(responseJson.error.toString());
        }
      }else{
        setMessage(""); // Clear input
        setErrorMessage(null);
      }
    }
  }
  return (
    <>
      <div className="flex flex-col h-full ">
        <header className="flex align-center">
          <b className="flex-1 bg-primary text-white text-center my-2">PUBLIC CHAT</b>
        </header>
        <div className="flex-2 h-full relative  overflow-auto"ref={chatContainerRef} onScroll={checkIfAtBottom}>
       
            {chatDetails?.messageList?.map((message, i) => {
              return (
                <div className={`chat text-gray-300 ${message.username !== chatDetails.username ? "chat-start":"chat-end w-full align-left"}`} key={i + message.username}
                >
                  <div className="chat-header">
                    
                    <div className="flex">
                      <div className="mr-2">{message.username}</div>
                      <time className="text-xs opacity-50 text-gray-500">
                      {message?.special ?? formatTimestamp(message.timestamp)}
                    </time>
                    </div>
                    
                  </div>
                  <div className={`chat-bubble ${message.username === chatDetails.username ? "chat-bubble-primary":"chat-bubble-secondary"}`}>{message.message}</div>
                </div>
              );
            }) ?? null}
             
            <div ref={messagesEndRef} />
            
          

        </div>
        {errorMessage &&(
                <button
                    onClick={() => {setErrorMessage(null);}}
                    style={{
                        position: 'absolute',
                        bottom: '70px',
                        left: '10px',
                        padding: '8px 12px',
                        background: 'red',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                    }}
                >
                    {errorMessage}
                </button>
            )}
        {!isAtBottomRef.current && isNewChat &&(
                <button
                    onClick={()=>{scrollToBottom();} }
                    className="absolute bottom-[70px] left-[10px] px-3 py-2 bg-blue-500 text-white border-none rounded cursor-pointer 
             hover:bg-blue-600 transition duration-300 
             animate-fade-in-up"
                >
                    New messages
                </button>
            )}

        <footer className="w-full px-4 py-2">
          {/* <p>username here</p> */}
          {chatStatus === "init" && (
            <button
              className="btn btn-primary w-full"
              onClick={(e) => {
                getPublicChat();
                setChatStatus("username");
                setHasJustLoadedChat(true);
                if(loggedInUser){
                  dispatch(setChatUsername(loggedInUser.username));
                }else{
                  let userNum = anonUserNumber ?? 0;
                  dispatch(setChatUsername("anonVisitor" + userNum));
                }
              }}
            >
              Send a message
            </button>
          )}
          {(chatStatus === "chat" || chatStatus === "username") && (
            <input
              type="text"
              placeholder="Send a message"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)}}
                onKeyDown={handleKeyDown}
              className="input input-bordered input-primary w-full max-w-xs"
            />
          )}
        </footer>
      </div>
    </>
  );
};

export default PublicChat;
